/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {ActiveProductContext, ImsOrg, ImsProfile} from './IMS';
import {Session} from '@adobe/exc-app/session';

export interface DiscoveryOptions {
  /**
   * Application ID.
   */
  appId: string;
  /**
   * Current base path.
   */
  basePath: string;
  /**
   * Application service code as it appears in the product context.
   */
  serviceCode?: string;
}

export enum CACHE_SCOPE {
  /**
   * Do not cache (Default).
   */
  NONE = 'NONE',
  /**
   * Cache in local storage
   */
  LOCAL = 'LOCAL'
}

export interface DiscoveryCacheConfig {
  /**
   * If true, path+hash will not be sent to the discovery service.
   * Instead the save path provided will be returned.
   * this allows better caching since all paths use the same cache entry.
   */
  ignorePath?: boolean;
  /**
   * Query parameters which may impact discovery result.
   * Any variation of these parameters will be kept in a seperate cache entry.
   */
  keepParams?: string[];
  /**
   * Query parameters which are ignored by the discovery service.
   * If part of the path, these parameters will be removed from the discovery result.
   */
  removeParams?: string[];
  /**
   * Discovery cache scope
   */
  scope: CACHE_SCOPE;
  /**
   * Time-to-live: How long is this cache entry valid for (seconds).
   */
  ttl?: number;
}

export interface DiscoverySource {
  /**
   * Discovery Cache Config
   */
  cache?: DiscoveryCacheConfig;
  defaults?: {[key: string]: string};
  /**
   * Discovery endpoint
   */
  discovery: string;
  /**
   * List of data to include in payload
   */
  payload?: string[];
  /**
   * If true, a random requestId will be added to the request for logging purposes.
   */
  requestId?: boolean;
  /**
   * Simple requests allow the browser to skip the OPTIONS call.
   * See: https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests
   */
  simpleRequest?: boolean;
  /**
   * Solution URL (Used when URL is known pre-discovery)
   */
  url?: string;
  /**
   * If true, include cookies in the discovery request
   */
  withCredentials?: boolean;
  /**
   * If true, client ID will be added to the request (x-api-key header).
   */
  xApiKey?: boolean;
}

export interface DiscoveryResponse {
  /**
   * IMS client ID to be used by solution
   */
  client_id?: string;
  /**
   * Error message (If discovery fails)
   */
  message?: string;
  /**
   * Token scope (Used with IMS client ID)
   */
  scope?: string;
  /**
   * Solution URL
   */
  url?: string;
  /**
   * Discovery URL used to get this response (For logging purposes).
   */
  discoveryUrl: string;
  /**
   * Timestamp (ms) when the discovery request was fulfilled
   */
  fulfilled: number;
  /**
   * Timestamp (ms) when the discovery response expires
   */
  expires: number;
}

export type DiscoveryPaths = {
  /**
   * Full path, including solution name.
   */
  fullPath?: string;
  /**
   * Path
   */
  path?: string;
};

export type DiscoveryPayload = DiscoveryPaths & {
  /**
   * Analytics Company (When using the Sub Org context)
   */
  company?: string;
  /**
   * Active product context - Used by Adobe Analytics to determine login company (sub org)
   */
  activeProductContext?: ActiveProductContext;
  /**
   * Current IMS org ID
   */
  imsOrg?: string;
  /**
   * Available IMS orgs (Not in use)
   */
  imsOrgs?: ImsOrg[];
  /**
   * IMS profile
   */
  imsProfile?: ImsProfile;
  /**
   * User Locale (Not in use)
   */
  locale?: string;
  /**
   * Tenant ID (Not in use)
   */
  tenant?: string;
};

export interface CachedSession extends Session {
  /**
   * Hashed value of the session context.
   * Used to verify if the session is valid for the current context.
   * i.e. if a session scope is a specific org, another org will yield a different hash.
   */
  hash: string;
}
